import React, { createContext, useContext, useMemo, useState } from "react";

import { mdiMenuOpen } from "@mdi/js";
import Icon from "@mdi/react";
import classNames from "classnames";

import AppVersion from "./AppVersion";
import MenuTitle from "./MenuTitle";
import MenuUser from "./MenuUser";
import {
  SIDEBAR_MENU_COLLAPSED_WIDTH,
  SIDEBAR_MENU_WIDTH,
} from "../../constants/size";
import Tooltip from "../tooltip/Tooltip";

import type { JSX, ReactElement } from "react";

interface ISidebarContext {
  isCollapsed: boolean;
  sidebarId: string;
}

interface SidebarProps {
  children: React.ReactNode;
  isCollapsable?: boolean;
  title?: string | ReactElement;
  id: string;
}

interface CollapseButtonProps {
  isCollapsed: boolean;
  isCollapsable?: boolean;
  onClick: () => void;
  title?: string | ReactElement;
}

const SidebarContext = createContext<ISidebarContext>({
  isCollapsed: false,
  sidebarId: "sidebar",
});

export function useSidebarContext(): ISidebarContext {
  return useContext(SidebarContext);
}

export const collapsedSidebarClassNames = (isCollapsed?: boolean): string =>
  isCollapsed
    ? "opacity-0"
    : "opacity-100 transition-opacity duration-300 ease-in-out delay-200";

function CollapseButton({
  isCollapsed,
  isCollapsable,
  onClick,
  title,
}: CollapseButtonProps): JSX.Element {
  const buttonClassNames = classNames(
    "absolute left-full w-[40px] cursor-pointer p-[0.46875rem] transition-translate duration-300 ease-in-out",
    isCollapsed ? "-translate-x-[calc(100%+25%)]" : "-translate-x-full",
  );

  return (
    <div className="relative flex h-[50px] items-center">
      {title ? (
        <MenuTitle
          collapsedSidebarClassNames={collapsedSidebarClassNames}
          isCollapsed={isCollapsed}
          title={title}
        />
      ) : null}
      {isCollapsable ? (
        <div className={buttonClassNames} onClick={onClick}>
          <Icon
            className="text-muted-foreground"
            path={mdiMenuOpen}
            rotate={isCollapsed ? 180 : 0}
            size={1}
          />
        </div>
      ) : null}
    </div>
  );
}

export default function Sidebar({
  children,
  isCollapsable,
  title,
  id,
}: SidebarProps): JSX.Element {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const sidebarClasses = classNames(
    "z-10 relative flex h-full shrink-0 flex-col justify-between bg-card drop-shadow-lg overflow-x-hidden w-(--width)",
    "transition-all duration-300 ease-in-out",
  );

  const contextValue: ISidebarContext = useMemo(
    () => ({ isCollapsed, sidebarId: id }),
    [isCollapsed, id],
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <div id={id} />
      <nav
        className={sidebarClasses}
        style={
          {
            "--width": isCollapsed
              ? `${SIDEBAR_MENU_COLLAPSED_WIDTH}px`
              : `${SIDEBAR_MENU_WIDTH}px`,
          } as React.CSSProperties
        }
      >
        <CollapseButton
          isCollapsable={isCollapsable}
          isCollapsed={isCollapsed}
          onClick={(): void => setIsCollapsed(!isCollapsed)}
          title={title}
        />
        {children}
        <MenuUser
          collapsedSidebarClassNames={collapsedSidebarClassNames}
          isCollapsed={isCollapsed}
        />
        <AppVersion
          collapsedSidebarClassNames={collapsedSidebarClassNames}
          isCollapsed={isCollapsed}
        />
      </nav>
      <Tooltip id="menu-tooltip" />
    </SidebarContext.Provider>
  );
}
